import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { USE_API } from '../../config/env';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchProductReviewsForProduct,
  selectReviewsForSlug,
} from '../../store/actions/reviewActions';
import ReviewSummary from './ReviewSummary';
import ReviewList from './ReviewList';
import ReviewForm from './ReviewForm';
import { summarizeReviews } from '../../utils/reviewSummary';

const Section = styled.section`
  margin-top: clamp(3rem, 6vw, 5rem);
  scroll-margin-top: 6rem;
`;

const Title = styled.h2`
  margin: 0 0 0.5rem;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(1.9rem, 2.8vw + 1rem, 3rem);
  font-weight: 300;
`;

const MutedNote = styled.p`
  margin: 0 0 1rem;
  font-size: ${({ theme }) => theme.type.bodySm};
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

/**
 * PDP reviews — API-backed via Redux.
 */
const ProductReviewsSection = forwardRef(function ProductReviewsSection(
  { productSlugOrId, productName, onMetaLoaded },
  ref,
) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const apiBucket = useAppSelector((s) => selectReviewsForSlug(s, productSlugOrId));
  const [showForm, setShowForm] = useState(false);

  const slug = productSlugOrId ?? '';

  useEffect(() => {
    if (!USE_API || !String(slug).trim()) return;
    dispatch(fetchProductReviewsForProduct(slug));
  }, [dispatch, slug]);

  const { reviews, summary } = useMemo(() => {
    if (!USE_API) return { reviews: [], summary: summarizeReviews([]) };
    const r = apiBucket?.reviews ?? [];
    const summ =
      apiBucket?.summary ??
      summarizeReviews(Array.isArray(r) ? r : []);
    return { reviews: Array.isArray(r) ? r : [], summary: summ };
  }, [apiBucket?.reviews, apiBucket?.summary]);

  const refreshSummary = useCallback(() => {
    if (USE_API && String(slug).trim()) dispatch(fetchProductReviewsForProduct(slug));
  }, [dispatch, slug]);

  useImperativeHandle(
    ref,
    () => ({
      openForm: () => setShowForm(true),
      refresh: refreshSummary,
    }),
    [refreshSummary],
  );

  useEffect(() => {
    onMetaLoaded?.({ count: summary.count, average: summary.average });
  }, [onMetaLoaded, summary.count, summary.average]);

  const liveSummary = USE_API && !apiBucket?.error;

  return (
    <Section ref={ref} id="product-reviews" aria-labelledby="product-reviews-heading">
      <Title id="product-reviews-heading">{t('modamai.reviews.sectionTitle')}</Title>
      {USE_API && apiBucket?.loading ? (
        <MutedNote>{t('modamai.reviews.loadingList')}</MutedNote>
      ) : null}
      {USE_API && apiBucket?.error ? (
        <MutedNote role="status">{apiBucket.error}</MutedNote>
      ) : null}
      <ReviewSummary
        summary={summary}
        onWriteReview={() => setShowForm((s) => !s)}
        liveFromApi={liveSummary}
      />
      {showForm ? (
        <ReviewForm
          productSlugOrId={slug}
          productName={productName}
          onCancel={() => setShowForm(false)}
          onSubmitted={() => refreshSummary()}
        />
      ) : null}
      <ReviewList reviews={reviews} />
    </Section>
  );
});

export default ProductReviewsSection;
